// Screen: "Friends & Rivalries"
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getFriends, sendFriendRequest, acceptFriendRequest, removeFriend, challengeFriend, getRivalries } from '../apiClient';
import EloBadge from '../components/EloBadge';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export default function FriendsPage() {
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [pending, setPending] = useState([]);
  const [rivalries, setRivalriesList] = useState([]);
  const [searchNickname, setSearchNickname] = useState('');
  const [message, setMessage] = useState('');
  const loadData = async () => {
    const friendData = await getFriends(token);
    setFriends(friendData.friends || []);
    setPending(friendData.pending || []);
    const rivalData = await getRivalries(token);
    setRivalriesList(Array.isArray(rivalData) ? rivalData : []);
  };

  useEffect(() => { if (token) loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSendRequest = async () => {
    if (!searchNickname.trim()) return;
    const result = await sendFriendRequest(token, null, searchNickname.trim());
    if (result.error) setMessage(result.error);
    else { setMessage('Friend request sent!'); setSearchNickname(''); loadData(); }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleAccept = async (friendshipId) => {
    await acceptFriendRequest(token, friendshipId);
    loadData();
  };

  const handleRemove = async (friendId) => {
    if (!window.confirm('Remove this friend?')) return;
    await removeFriend(token, friendId);
    loadData();
  };

  const handleChallenge = async (friendId) => {
    const result = await challengeFriend(token, friendId);
    if (result.success) setMessage('Challenge sent!');
    else setMessage(result.error || 'Failed to send challenge');
    setTimeout(() => setMessage(''), 3000);
  };

  const tabStyle = (t) => ({
    background: tab === t ? '#238636' : 'transparent',
    color: tab === t ? '#fff' : '#8b949e',
    border: tab === t ? 'none' : '1px solid #30363d',
    borderRadius: 6, padding: isMobile ? '7px 14px' : '8px 18px',
    fontSize: isMobile ? 12 : 13, cursor: 'pointer', fontWeight: 600
  });

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 600, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: 16 }}>Friends & Rivals</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setTab('friends')} style={tabStyle('friends')}>
          Friends ({friends.length})
        </button>
        <button onClick={() => setTab('pending')} style={tabStyle('pending')}>
          Pending ({pending.length})
        </button>
        <button onClick={() => setTab('rivalries')} style={tabStyle('rivalries')}>
          Rivalries ({rivalries.length})
        </button>
      </div>

      {message && (
        <div style={{ background: '#1c2333', border: '1px solid #30363d', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#58a6ff' }}>
          {message}
        </div>
      )}

      {/* Friends Tab */}
      {tab === 'friends' && (
        <>
          {/* Add friend search */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              type="text"
              value={searchNickname}
              onChange={e => setSearchNickname(e.target.value)}
              placeholder="Add friend by nickname..."
              onKeyDown={e => e.key === 'Enter' && handleSendRequest()}
              style={{
                flex: 1, background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
                color: '#e2e8f0', padding: '8px 12px', fontSize: 13
              }}
            />
            <button onClick={handleSendRequest} style={{
              background: '#238636', color: '#fff', border: 'none', borderRadius: 6,
              padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600
            }}>Add</button>
          </div>

          {friends.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>
              No friends yet. Search by nickname to add friends!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {friends.map(f => (
                <div key={f.friendshipId} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  background: '#161b22', border: '1px solid #30363d', borderRadius: 8
                }}>
                  <EloBadge points={f.rankPoints || 0} preferredSuit={f.preferredSuit || '♠'} size="sm" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#c9d1d9' }}>{f.nickname}</div>
                    <div style={{ fontSize: 10, color: '#8b949e' }}>{f.rankName || 'Nine'} &middot; {f.rankPoints || 0} pts</div>
                  </div>
                  <button onClick={() => handleChallenge(f.userId)} style={{
                    background: '#1c2333', color: '#58a6ff', border: '1px solid #30363d',
                    borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600
                  }}>Challenge</button>
                  <button onClick={() => handleRemove(f.userId)} style={{
                    background: 'transparent', color: '#f85149', border: '1px solid #f8514930',
                    borderRadius: 6, padding: '5px 8px', fontSize: 10, cursor: 'pointer'
                  }}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Pending Tab */}
      {tab === 'pending' && (
        pending.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>
            No pending friend requests.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pending.map(p => (
              <div key={p.friendshipId} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                background: '#161b22', border: '1px solid #30363d', borderRadius: 8
              }}>
                <EloBadge points={p.rankPoints || 0} preferredSuit={p.preferredSuit || '♠'} size="sm" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#c9d1d9' }}>{p.nickname}</div>
                  <div style={{ fontSize: 10, color: '#8b949e' }}>{p.rankName || 'Nine'} &middot; {p.rankPoints || 0} pts</div>
                </div>
                <button onClick={() => handleAccept(p.friendshipId)} style={{
                  background: '#238636', color: '#fff', border: 'none',
                  borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600
                }}>Accept</button>
              </div>
            ))}
          </div>
        )
      )}

      {/* Rivalries Tab */}
      {tab === 'rivalries' && (
        rivalries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>
            No rivalries yet. Play multiplayer games to build head-to-head records!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rivalries.map(r => {
              const total = r.myWins + r.theirWins;
              const myPct = total > 0 ? Math.round((r.myWins / total) * 100) : 50;
              return (
                <div key={r.rivalryId} style={{
                  background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
                  padding: '12px 16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#c9d1d9' }}>vs {r.opponentNickname}</span>
                    <span style={{ fontSize: 10, color: '#8b949e' }}>{r.gamesPlayed} games</span>
                  </div>
                  {/* Win bar */}
                  <div style={{ display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ width: `${myPct}%`, background: '#238636', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 600, minWidth: 30 }}>
                      {r.myWins}W
                    </div>
                    <div style={{ width: `${100 - myPct}%`, background: '#f85149', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 600, minWidth: 30 }}>
                      {r.theirWins}W
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: '#8b949e' }}>
                    {r.opponentRank || 'Nine'} &middot; Last played: {r.lastGameAt ? new Date(r.lastGameAt).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
